import { Box, Link as MuiLink, Typography } from "@mui/material";
import type { JSX } from "react";
import { Link } from "react-router-dom";

import { relativeTime } from "../../../../lib/relativeTime.js";
import type { SupportTicket } from "../../types.js";

/**
 * Ticket title row — back arrow, reference (mono), em-dash, title — all on the
 * same line, with an org · reporter · created subline. Status lives only in the
 * Details panel (keeping it out of the title row avoids two pills showing the
 * same value). Extracted from `TicketDetailPage.tsx`.
 */
export function TicketHeader({ ticket: t }: { ticket: SupportTicket }): JSX.Element {
  return (
    <Box component="header" sx={{ display: "grid", gap: 0.5, borderBottom: 1, borderColor: "divider", pb: 1.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        <MuiLink
          component={Link}
          to="/support/tickets"
          aria-label="All tickets"
          title="All tickets"
          underline="none"
          sx={{
            display: "inline-flex",
            height: 28,
            width: 28,
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1,
            color: "text.disabled",
            "&:hover": { bgcolor: "surfaceSoft", color: "text.primary" },
          }}
        >
          <Box component="svg" viewBox="0 0 16 16" aria-hidden sx={{ height: 16, width: 16 }}>
            <path
              d="M10 3L5 8l5 5"
              stroke="currentColor"
              strokeWidth="1.75"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Box>
        </MuiLink>
        <Box component="span" sx={{ fontFamily: "ui-monospace, monospace", fontSize: 14, color: "text.disabled" }}>
          {t.reference}
        </Box>
        <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
        <Typography
          component="h2"
          sx={{ minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "1.25rem", fontWeight: 600, letterSpacing: "-0.01em", color: "text.primary" }}
        >
          {t.title}
        </Typography>
      </Box>
      <Typography sx={{ pl: 4.5, fontSize: 12, color: "text.disabled" }}>
        {t.organization?.name ?? "—"}
        {t.reporter?.name && (
          <>
            {" · reported by "}
            <Box component="span" sx={{ color: "text.primary" }}>
              {t.reporter.name}
            </Box>
          </>
        )}
        {t.created_at && (
          <>
            {" · "}
            <RelativeTime iso={t.created_at} prefix="created" />
          </>
        )}
      </Typography>
    </Box>
  );
}

function RelativeTime({
  iso,
  prefix,
}: {
  iso: string;
  prefix?: string;
}): JSX.Element {
  const date = new Date(iso);
  return (
    <Box component="span" title={date.toLocaleString()}>
      {prefix ? `${prefix} ` : ""}
      {relativeTime(date)}
    </Box>
  );
}
